import { useProductStore } from '#product/app/stores/product'
import { QUERY_REVALIDATE } from '~/constants/queryParams'

export default defineNuxtRouteMiddleware(async (to, from) => {
  if (import.meta.server) return // fallback if route is not ssr: false in nuxt.config routeRules

  const { isLoggedIn } = useAuth()
  const licensedTerritory = useLicensedTerritory({ raw: true })
  const productName = useProductGetter().getProductFromRoute(to)
  const { getProduct } = useProductStore()
  const { navigateToLocalized } = useRouterUtils()
  const { log } = useDatadog()

  const navigateToCheckout = (p: string) =>
    navigateToLocalized({
      path: `/checkout?p=${p.toLowerCase()}`,
      query: to.query,
      replace: true,
    })

  if (!productName || typeof productName !== 'string' || !isLoggedIn.value) {
    return navigateToLocalized({
      path: '/',
      replace: true,
    })
  }

  if (to.query.replay === 'true') return navigateToCheckout(productName)

  const isScratchcard = to.query.scratchcard === '1'
  if (isScratchcard) {
    const quantity = to.query.quantity
    if (typeof quantity !== 'string') return

    const licensedTerritory = useLicensedTerritory({ raw: true })
    const { mutate } = useReserveScratchcardsMutation()
    const result = await mutate({
      input: {
        gameSlug: productName,
        licensedTerritory,
        quantity: parseInt(quantity),
      },
    })

    if (
      !result?.data?.reserveScratchcards.reservationId ||
      !!result?.data?.reserveScratchcards.restrictions
    ) {
      return await navigateToLocalized(
        {
          path: String(to.query?.backroute ?? '/'),
          query: {
            [QUERY_REVALIDATE]: 'true',
          },
        },
        { replace: true },
      )
    }

    useProductStore().setProduct(
      result.data.reserveScratchcards.reservationId,
      productName,
      ProductType.Scratchcard,
    )

    return navigateToCheckout(productName)
  }

  const { doesExist, getHighFrequencyGameIdInCorrectCase } =
    useHighFrequencyGamesOfLicensedTerritory()

  const isHighFrequencyGame = doesExist(productName)

  if (isHighFrequencyGame) {
    const gameId = getHighFrequencyGameIdInCorrectCase(productName)
    await useHighFrequencyGameConfigLazyQuery({
      gameId,
      licensedTerritory,
    }).load()

    const playslipStored = await useHighFrequencyGame(gameId as string).storeHighFrequencyGameSlip()

    if (playslipStored.type !== 'stored') {
      const path = typeof to.query?.backroute === 'string' ? to.query.backroute : from.fullPath

      return await navigateToLocalized({
        path,
        query: {
          [QUERY_REVALIDATE]: 'true',
        },
        replace: true,
      })
    }

    return navigateToCheckout(productName)
  }

  try {
    await useLotteryLazyQuery({
      licensedTerritory,
      lotteryId: getLotteryIdInCorrectCase(productName),
    }).load()

    await usePlaySlip(productName).storePlaySlip()
    return navigateToCheckout(productName)
  } catch (error) {
    if (
      isSpecificGraphQLError(error, {
        matchMessage: 'validation.subscription.notallowed',
      })
    ) {
      return navigateToLocalized({
        path: to.query.backroute ? String(to.query.backroute) : `/${productName}/options`,
        query: {
          [QUERY_REVALIDATE]: 'true',
        },
      })
    }

    if (
      isSpecificGraphQLError(error, {
        matchMessage: 'validation.playslip.duplicate',
      })
    ) {
      log('warn', {
        appendPlayerInfo: true,
        component: 'checkout-redirect',
        message: 'Duplicate play slip replay attempt',
        messageContext: {
          product: getProduct(productName),
          productName,
        },
      })
    }
    if (typeof to.query.backroute !== 'string') return

    return navigateToLocalized({
      path: to.query.backroute,
      query: {
        [QUERY_REVALIDATE]: 'true',
      },
    })
  }
})
